import { Component, Output, EventEmitter, Input } from '@angular/core';
import { AggregationDataService } from '../aggregation-data.service';
import { AutoUnsubscribe } from 'src/app/shared/decorators/AutoUnsubscribe';
import { Observable, of, forkJoin } from 'rxjs';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { IgnoreProduct } from 'src/app/shared/classes/models/IgnoreProduct';
import { Uuid } from 'src/app/shared/classes/uuid';
import { AggregationResultService } from 'src/app/shared/api/services';
import { ClientBillingAggregationResult } from 'src/app/shared/api/models';
import { AggregationResultStatus } from 'src/app/shared/enums/AggregationResultStatus';
import { ConfirmationDialogService } from 'src/app/bulk-usage/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'e2cb-aggregation-detail',
  templateUrl: './aggregation-detail.component.html',
  styleUrls: ['./aggregation-detail.component.scss']
})
@AutoUnsubscribe
export class AggregationDetailComponent {
  // --------------------------------------------------

  // #region Public Members
  public AggregationResultStatus = AggregationResultStatus; // must assign for Scope use in HTML template
  public showSpinner: boolean = false;
  public testClients: any[] =["1111", "2222", "3333"];

  @Input() hasPermissionToEdit;
  @Output() loadingChange = new EventEmitter<boolean>(false);
  @Output() updateCurrentAggregation = new EventEmitter<ClientBillingAggregationResult>();
  @Output() onDataChange = new EventEmitter<string>(false);
  @Output() OnRelatedClientChange = new EventEmitter<string>();

  public showIgnoreColumn: boolean = false;
  public tab_AEOrIgnored = 'ae';
  selectedRelatedClient = "";
  prodDescExceptionRows: any[] = [];
  exceptions: any[] = [];
  ignoredExceptions: any[] = [];
  aggregationRows: any[] = [];
  status: string = "";
  clientId: string = "";
  month: number;
  year: number;
  ignores !: any[];
  private _uniqueId: string;
  // #endregion

  // --------------------------------------------------

  // #region Readonly Members
  aggregationColumns: any[] = [
    { name: 'Product Description', prop: 'prodDesc', width: 675 },
    { name: 'Category', prop: 'category', width: 200 },
    { name: 'Count', prop: 'count', width: 100 }
  ];
  rowHeight: number = 50;
  // #endregion

  // --------------------------------------------------

  // #region Constructors
  constructor(public sharedDataService: SharedDataService,
    private _dataService: AggregationDataService,
    private _aggregationResultService: AggregationResultService,
    private confirmationDialogService: ConfirmationDialogService) { }
  // #endregion

  // --------------------------------------------------

  // #region Public Methods
  public Initialize(id: string): void {
    this._uniqueId = id;
    this.aggregationRows = [];
    this.ignoredExceptions = [];
    this.exceptions = [];

    /*
      get the current aggregation and variables for setting our members.
      If no aggregations/Exceptions for selected relatedClientId/clientId,
      handle undefined values so clientId will display and not data in the grids
    */
    const aggregation = this.FetchAggregationResult();
    this.showIgnoreColumn = aggregation.status === AggregationResultStatus.New;

    let ignores = this.sharedDataService.ignoreProducts.filter(elem => elem.aviClientId === aggregation.clientId);

    // billing agreement exceptions.
    aggregation.billingAgreementExceptions.forEach(elem => {
      const ignore = ignores.find(val => val.productDescription === elem.billingAgreement);
      if (ignore) {
        this.ignoredExceptions.push({
          prodDesc: elem.billingAgreement,
          count: elem.exceptionCount
        });
      } else {
        this.exceptions.push({
          prodDesc: elem.billingAgreement,
          count: elem.exceptionCount
        });
      }
    });

    // aggregation results.
    aggregation.aggregations.forEach(elem => {
      this.aggregationRows.push({
        prodDesc: elem.productDescription,
        category: elem.invoiceLineItemCategory,
        count: elem.value
      });
    });

    this.clientId = aggregation.clientId;
    // status.
    this.status = aggregation.status;
    // Month and Year
    this.month = aggregation.month;
    this.year = aggregation.year;

    this.aggregationRows = [...this.aggregationRows];
  }

  async HandleResolution(resolution: string) {
    this.showSpinner = true;
    this.loadingChange.emit(this.showSpinner);

    switch (resolution) {
      case "complete":
        {
          // get the need to add and remove.
          let needToAdd: IgnoreProduct[] = this.FindIgnoreProductsToAdd();
          let needToRemove: string[] = this.FindIgnoreProductsToRemove();

          // call the services.
          forkJoin(this._dataService.resolveAsComplete(this._uniqueId),
            this.RectifyIgnoresWithSharedData(needToAdd, needToRemove)).subscribe(
              res => {
                // get the responses.
                let aggRes = res[0];
                let ignoreRes = res[1];

                // HANDLE AGGREGATION RESULT COMPLETE RESPONSE.
                if (!aggRes.success) {
                  this.sharedDataService.HandleError(aggRes.errors);
                } else {
                  const aggResult = this.FetchAggregationResult();
                  aggResult.status = AggregationResultStatus.Completed;
                  this.sharedDataService.aggregationResults = [... this.sharedDataService.aggregationResults];
                }

                // HANDLE IGNORE PRODUCT RESPONSE.
                if (!ignoreRes) {
                  this.sharedDataService.HandleError("Error during Ignore Product Bulk Operation");
                  return;
                }

                // add to the ignore products.
                this.sharedDataService.ignoreProducts = this.sharedDataService.ignoreProducts.concat(needToAdd);

                // remove from the ignore products.
                needToRemove.forEach(id => {
                  let idx = this.sharedDataService.ignoreProducts.findIndex(ip => ip.clientBillingIgnoreProductId === id);
                  this.sharedDataService.ignoreProducts.splice(idx, 1);
                });

                this.onDataChange.emit("updated");
                this.ClearTables();
              },
              error => {
                this.sharedDataService.HandleError(error);
                this.showSpinner = false;
                this.loadingChange.emit(this.showSpinner);
              },
              () => {
                this.showSpinner = false;
                this.loadingChange.emit(this.showSpinner);
              }
            );
        } break;

      case "rerun":
        {
          let needToAdd: IgnoreProduct[] = this.FindIgnoreProductsToAdd();
          let needToRemove: string[] = this.FindIgnoreProductsToRemove();
          var ignoreRes: any;
          var joinedResults: any;
          try {
            joinedResults = await forkJoin(
              this._aggregationResultService.Rerun({ clientMonthYearDTO: { month: this.month, year: this.year, clientId: this.clientId } }),
              this.RectifyIgnoresWithSharedData(needToAdd, needToRemove)
            ).toPromise();
            ignoreRes = joinedResults[1];
          } catch (e) {
            this.sharedDataService.HandleError(e);
            this.showSpinner = false;
            this.loadingChange.emit(this.showSpinner);
          }

          const idx = this.sharedDataService.aggregationResults.findIndex(elem => elem.id === this._uniqueId);
          this.sharedDataService.aggregationResults.splice(idx, 1);

          // see if we have bad data.
          if (!ignoreRes) {
            this.sharedDataService.HandleError("Error during Re-Run.");
            return;
          }

          this.sharedDataService.ignoreProducts = this.sharedDataService.ignoreProducts.concat(needToAdd);

          // remove from the ignore products.
          needToRemove.forEach(id => {
            let idx = this.sharedDataService.ignoreProducts.findIndex(ip => ip.clientBillingIgnoreProductId === id);
            this.sharedDataService.ignoreProducts.splice(idx, 1);
          });

          this.showSpinner = false;
          this.loadingChange.emit(this.showSpinner);
          this.onDataChange.emit("updated");
          this.ClearTables();

        } break;

      case "reaggregate":
        {
          let needToAdd: IgnoreProduct[] = this.FindIgnoreProductsToAdd();
          let needToRemove: string[] = this.FindIgnoreProductsToRemove();
          var ignoreRes: any;
          var joinedResults: any;
          try {
            joinedResults = await forkJoin(
              this._aggregationResultService.RerunAggregationResult({ clientMonthYearDTO: { month: this.month, year: this.year, clientId: this.clientId } }),
              this.RectifyIgnoresWithSharedData(needToAdd, needToRemove)
            ).toPromise();
            ignoreRes = joinedResults[1];
          } catch (e) {
            this.sharedDataService.HandleError(e);
            this.showSpinner = false;
            this.loadingChange.emit(this.showSpinner);
          }

          const idx = this.sharedDataService.aggregationResults.findIndex(elem => elem.id === this._uniqueId);
          this.sharedDataService.aggregationResults.splice(idx, 1);

          // see if we have bad data.
          if (!ignoreRes) {
            this.sharedDataService.HandleError("Error during Re-aggregate");
            return;
          }

          this.sharedDataService.ignoreProducts = this.sharedDataService.ignoreProducts.concat(needToAdd);

          // remove from the ignore products.
          needToRemove.forEach(id => {
            let idx = this.sharedDataService.ignoreProducts.findIndex(ip => ip.clientBillingIgnoreProductId === id);
            this.sharedDataService.ignoreProducts.splice(idx, 1);
          });

          this.showSpinner = false;
          this.loadingChange.emit(this.showSpinner);
          this.onDataChange.emit("updated");
          this.ClearTables();

        } break;

      default:
        break;
    }
  }

  HandleIgnoreRowClick(rowData: any, rowIdx: number): void {
    // update the visual.
    this.exceptions.splice(rowIdx, 1);
    this.ignoredExceptions.push(rowData);

    this.exceptions = [...this.exceptions];
    this.ignoredExceptions = [...this.ignoredExceptions];
  }

  HandleUnignoreRowClick(rowData: any, rowIdx: number): void {
    this.ignoredExceptions.splice(rowIdx, 1);
    this.exceptions.push(rowData);

    this.exceptions = [...this.exceptions];
    this.ignoredExceptions = [...this.ignoredExceptions];
  }

  HandleIgnoreAllExceptionsClicked() {
    this.ignoredExceptions = this.ignoredExceptions.concat(this.exceptions.splice(0));
    this.exceptions = [];
  }

  ClearTables() {
    this.clientId = '';
    this.aggregationRows = [];
    this.exceptions = [];
    this.ignoredExceptions = [];
    this.status = "";
  }

  UpdateAggregateResult(clientId, updatedState) {
    let toUpdate = { clientId: clientId, status: updatedState } as ClientBillingAggregationResult;

    if (updatedState !== AggregationResultStatus.Completed) {
      this.updateCurrentAggregation.emit(toUpdate);
      this.ClearTables();
      return;
    }

    this.confirmationDialogService.confirm(
      'Reset Aggregation State to Completed',
      "This will reset the aggregation state to 'Completed'. This cannot be reverted.",
      'Confirm',
      'Cancel'
    ).then((confirmed) => {
      if (confirmed) {
        this.updateCurrentAggregation.emit(toUpdate);
        this.ClearTables();
      }
    })
      .catch(() => console.log('User dismissed the dialog'));
  }
  // #endregion

  // #region Private Methods
  private FindIgnoreProductsToAdd(): IgnoreProduct[] {
    // get all the ignores we know of for this client.
    const sharedDataIgnores = this.sharedDataService.ignoreProducts.filter(elem => elem.aviClientId === this.clientId);

    // search for any ignores that have been added.
    const needToAdd: IgnoreProduct[] = [];
    this.ignoredExceptions.forEach(ignoredException => {
      const matchingException = sharedDataIgnores.find((elem => elem.productDescription === ignoredException.prodDesc));
      if (!matchingException) {
        // we know we have something to add.
        needToAdd.push({
          productDescription: ignoredException.prodDesc,
          aviClientId: this.clientId,
          clientBillingIgnoreProductId: Uuid.createV4()
        });
      }
    });

    return needToAdd;
  }

  private FindIgnoreProductsToRemove(): string[] {
    // get all the ignores we know of for this client.
    const sharedDataIgnores = this.sharedDataService.ignoreProducts.filter(elem => elem.aviClientId === this.clientId);

    // search for any ignores that have been removed.
    const needToRemove: string[] = [];
    this.exceptions.forEach(exception => {
      const matchingException = sharedDataIgnores.find((elem => elem.productDescription === exception.prodDesc));
      if (matchingException) {
        // we know we have something that needs to be removed.
        needToRemove.push(matchingException.clientBillingIgnoreProductId);
      }
    });

    return needToRemove;
  }

  private RectifyIgnoresWithSharedData(needToAdd: IgnoreProduct[], needToRemove: string[]): Observable<any> {
    if (needToAdd.length === 0 && needToRemove.length === 0) {
      return of<any>({
        success: true,
        data: {}
      });
    }
    return this._dataService.bulkIgnoreProduct(needToAdd, needToRemove);
  }

  private FetchAggregationResult(): ClientBillingAggregationResult {
    return this.sharedDataService.aggregationResults.find(elem => elem.id === this._uniqueId);
  }

  CallParentClientChange(clientIdSelected: string)
  {
    this.selectedRelatedClient = clientIdSelected;
    this.OnRelatedClientChange.emit(clientIdSelected);
  }
  // #endregion
}
